import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { AppHeader } from "@/components/app-header";
import { AppSidebar } from "@/components/app-sidebar";
import { FirstManagedSiteBanner } from "@/components/first-managed-site-banner";
import { HeaderBreadcrumbs } from "@/components/header-breadcrumbs";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUser } from "@/lib/user-context";
import { trpc } from "@/utils/trpc";

const DASHBOARD_PATH = "/dashboard";

export function AppShell({ children }: { children: ReactNode }) {
  const { isManager, session, systemRole } = useUser();

  const managedSitesQuery = useQuery({
    ...trpc.getManagedSites.queryOptions(),
    enabled: isManager,
  });
  const subscriptionOverviewQuery = useQuery({
    ...trpc.getSubscriptionOverview.queryOptions(),
    enabled: isManager,
  });
  const overview = subscriptionOverviewQuery.data;
  const hasActiveSubscription =
    overview?.billingState?.hasActivePaidSubscription ||
    overview?.bypass?.active;
  const showUpgradeButton = isManager && !hasActiveSubscription;
  const currentPlanName = overview?.plan.name;
  const showFirstManagedSiteBanner =
    isManager &&
    Array.isArray(managedSitesQuery.data) &&
    managedSitesQuery.data.length === 0;

  const fullName = session?.user.name?.trim() || "OS04 User";
  const initials = fullName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <SidebarProvider className="app-surface h-svh flex-col min-h-0">
      <AppHeader
        user={{
          name: fullName,
          planName:
            isManager && currentPlanName ? currentPlanName.toLowerCase() : null,
          initials,
        }}
        showUpgradeButton={showUpgradeButton}
        showSubscriptionLink={systemRole === "site_manager"}
      />
      <div className="flex flex-1 min-h-0">
        <AppSidebar />
        <SidebarInset className="min-h-0 overflow-y-auto overscroll-y-none bg-transparent">
          <div className="sticky top-0 z-20 border-b border-border/60 bg-background/72 backdrop-blur-xl supports-[backdrop-filter]:bg-background/62">
            <div className="flex items-center gap-3 px-4 py-3 md:px-6 lg:px-8">
              <HeaderBreadcrumbs
                dashboardPath={DASHBOARD_PATH}
                className="flex-1"
              />
            </div>
          </div>

          <div className="flex-1">
            {showFirstManagedSiteBanner ? <FirstManagedSiteBanner /> : null}
            {children}
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
